import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Brain, Eye, Hand, Heart, ArrowRight } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

const benefits = [
  {
    icon: Brain,
    title: 'Cognitive Development',
    description:
      'The mouth is densely innervated with multiple receptor classes. When your baby mouths a textured teether, they gain meaningful, multi-attribute information — learning about hardness, shape, and surface all at once. This rich sensory input helps build neural pathways during the critical first year.',
  },
  {
    icon: Eye,
    title: 'Visual Familiarisation',
    description:
      'Studies show that repeated visual exposure to food shapes builds recognition and reduces later food hesitancy. Our real-food forms create a familiarity scaffold — connecting what your baby sees and mouths with the foods they will encounter at the table.',
  },
  {
    icon: Hand,
    title: 'Fine Motor Skills',
    description:
      'Different grip zones across each teether shape encourage varied hand positions. The stem of a carrot, the curve of a banana, the round of an apple — each invites a different grasp, strengthening the muscles needed for self-feeding and later fine motor tasks.',
  },
  {
    icon: Heart,
    title: 'Positive Food Associations',
    description:
      'Research shows that emotionally neutral or positive repeated exposures build stable associations that can make later tasting easier — particularly for vegetables. Our teethers create calm, enjoyable food encounters long before solids begin.',
  },
]

const researchPoints = [
  {
    stat: '5 senses',
    description:
      'The mouth engages simultaneously — detecting texture, temperature, shape, hardness, and form in a single mouthing.',
  },
  {
    stat: 'Highest density',
    description:
      'The lips and oral cavity are among the most densely innervated areas of the body, with distinct cortical mapping for lips, tongue, and teeth.',
  },
  {
    stat: '10+ exposures',
    description:
      'Studies suggest repeated low-pressure exposure to food shapes builds the recognition that supports later food acceptance.',
  },
]

const firstFoods = [
  {
    name: 'Apple',
    slug: 'apple-explorer',
    color: 'bg-pp-blush',
    colorSoft: 'bg-pp-blush-soft',
    rationale:
      'The most consumed fruit for infants and the most common single-ingredient fruit in commercial baby food. Over half of mixed baby food products contain apple. Parents describe choosing apple early because it feels gentle and beneficial.',
  },
  {
    name: 'Banana',
    slug: 'banana-buddy',
    color: 'bg-pp-butter',
    colorSoft: 'bg-pp-butter/20',
    rationale:
      'Among the most consumed first fruits worldwide, culturally embedded as a safe, nutritious starter food. Its distinctive shape and colour make it one of the most visually recognisable foods for infants and parents alike.',
  },
  {
    name: 'Carrot',
    slug: 'carrot-companion',
    color: 'bg-pp-terracotta',
    colorSoft: 'bg-pp-terracotta-soft',
    rationale:
      'The most common single-ingredient vegetable in commercial baby foods. When vegetable-only first foods exist, they are most often sweet vegetables like carrot — the vegetable parents most readily accept as a first step.',
  },
  {
    name: 'Broccoli',
    slug: 'broccoli-buddy',
    color: 'bg-pp-sage',
    colorSoft: 'bg-pp-sage-soft',
    rationale:
      'Offered as a first food for over a quarter of babies and frequently given as a finger food — yet underrepresented in commercial baby products. This market gap makes broccoli our aspirational vegetable.',
  },
]

export default function Science() {
  return (
    <div className="min-h-screen bg-pp-ivory">
      <SEO
        title="The Science"
        description="Discover the developmental science behind Petite Palate teethers. Learn how oral sensory exploration and visual food familiarisation support your baby's growth, and why we chose apple, banana, and carrot as our first food shapes."
        url="/science"
      />

      {/* Hero */}
      <section className="bg-pp-linen py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-pp-terracotta font-body text-sm tracking-widest uppercase mb-4">
              The Science Behind Our Design
            </p>
            <h1 className="font-display text-display-lg text-pp-ink mb-6">
              Every texture tells a story
            </h1>
            <p className="text-pp-stone text-lg leading-relaxed">
              Our teethers are designed around developmental science — from the
              dense innervation of the infant mouth to the power of visual
              familiarity in building food acceptance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Oral Exploration */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-display-md text-pp-ink mb-6">
                Why Oral Exploration Matters
              </h2>
              <div className="space-y-4 text-pp-stone leading-relaxed">
                <p>
                  From birth, mouthing is a fundamental route for infants to gain
                  haptic experience of the world. This isn't random chewing — it's
                  active exploration. The face, especially the lips, and the oral
                  cavity are among the most densely innervated areas of the body,
                  packed with multiple receptor classes that detect touch,
                  temperature, texture, and shape simultaneously.
                </p>
                <p>
                  Neuroimaging studies have mapped distinct cortical
                  representations for the lips, tongue, and teeth within the
                  brain's somatosensory cortex — confirming that oral inputs are
                  treated as high-value information. The vermilion region of the
                  lips is particularly receptive to both touch and temperature,
                  which is why babies instinctively bring objects to their mouths.
                </p>
                <p>
                  Research shows that infants can gain meaningful, multi-attribute
                  information quickly when an object is in the mouth — learning
                  about hardness, softness, surface texture, and three-dimensional
                  form in a single exploration. Our teethers are designed to
                  maximise this learning, with carefully graduated textures that
                  introduce complexity as your baby develops.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-3 gap-4"
            >
              {researchPoints.map((point, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border border-pp-sand/50 text-center"
                >
                  <p className="font-display text-2xl text-pp-terracotta mb-2">
                    {point.stat}
                  </p>
                  <p className="text-pp-stone text-xs leading-relaxed">
                    {point.description}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visual Familiarisation & Food Acceptance */}
      <section className="py-16 lg:py-24 bg-pp-linen">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-pp-sand/30 rounded-2xl aspect-[4/3] flex items-center justify-center">
                <p className="text-pp-stone/50 text-sm">Visual familiarisation illustration</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="font-display text-display-md text-pp-ink mb-6">
                How Familiar Shapes Support Food Acceptance
              </h2>
              <div className="space-y-4 text-pp-stone leading-relaxed">
                <p>
                  Research with toddlers has shown that simply sharing picture
                  books featuring specific fruits and vegetables led to less
                  hesitant behaviour and greater willingness to taste those foods.
                  The core mechanism is straightforward: repeated, low-pressure
                  exposure builds recognition and predictability, making later
                  tasting easier — particularly for vegetables.
                </p>
                <p>
                  This effect works across formats. Whether through shared
                  reading, digital picture books, or physical objects, the
                  consistent finding is that visual familiarity with a food
                  reduces the novelty barrier when that food appears on the plate.
                </p>
                <p>
                  Our teethers apply this same principle in three dimensions. By
                  shaping each teether as a faithful representation of a real food
                  — with accurate colour, recognisable form, and realistic
                  proportions — we create a familiarity scaffold. Months of calm,
                  positive mouthing builds stable associations between the teether
                  shape and real food, supporting the transition to solids.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-display-md text-pp-ink mb-4">
              Developmental Benefits
            </h2>
            <p className="text-pp-stone max-w-lg mx-auto">
              Each Petite Palate teether is designed to support multiple areas
              of development simultaneously.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-pp-sand/50"
              >
                <div className="w-12 h-12 rounded-xl bg-pp-terracotta/10 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-pp-terracotta" />
                </div>
                <h3 className="font-display text-lg text-pp-ink mb-2">
                  {benefit.title}
                </h3>
                <p className="text-pp-stone text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Texture System */}
      <section className="py-16 lg:py-24 bg-pp-linen">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-display text-display-md text-pp-ink mb-4 text-center">
              Our Graduated Texture System
            </h2>
            <p className="text-pp-stone text-center max-w-2xl mx-auto mb-8">
              Research highlights a market drift toward uniformly smooth purée
              foods in pouches. Our graduated texture system is designed to
              counter this by progressively introducing the varied surfaces
              babies need for healthy oral-motor development.
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-pp-sage-soft flex items-center justify-center flex-shrink-0">
                    <span className="text-pp-sage font-medium">1</span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-pp-ink mb-2">
                      Soft & Gentle (0-3 months)
                    </h3>
                    <p className="text-pp-stone text-sm">
                      Smooth, yielding surfaces designed for the densely
                      innervated lips and new mouths. Perfect for early
                      exploration, gently stimulating oral receptors without
                      overwhelming sensitive tissue.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-pp-terracotta-soft flex items-center justify-center flex-shrink-0">
                    <span className="text-pp-terracotta font-medium">2</span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-pp-ink mb-2">
                      Textured & Stimulating (3-6 months)
                    </h3>
                    <p className="text-pp-stone text-sm">
                      Varied bumps, ridges, and patterns that engage multiple
                      receptor classes simultaneously. These textures massage
                      emerging gums while building the sensory awareness needed
                      to process more complex food textures later.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-pp-blush-soft flex items-center justify-center flex-shrink-0">
                    <span className="text-pp-blush font-medium">3</span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-pp-ink mb-2">
                      Complex & Challenging (6+ months)
                    </h3>
                    <p className="text-pp-stone text-sm">
                      Multi-textured surfaces that encourage active exploration
                      and prepare babies for the varied textures of real food —
                      countering the uniformity of commercial purées and
                      supporting the transition to self-feeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why We Start With These Foods */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-display-md text-pp-ink mb-4">
              Why We Start With These Foods
            </h2>
            <p className="text-pp-stone max-w-2xl mx-auto">
              We didn't choose our shapes at random. Each teether in our
              collection represents one of the most common, most trusted first
              foods — backed by consumption data, market research, and parent
              behaviour studies.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {firstFoods.map((food, index) => (
              <motion.div
                key={food.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-pp-sand/50 flex flex-col"
              >
                <div className={`w-12 h-12 rounded-xl ${food.colorSoft} flex items-center justify-center mb-4`}>
                  <span className="font-display text-lg text-pp-ink">
                    {food.name[0]}
                  </span>
                </div>
                <h3 className="font-display text-lg text-pp-ink mb-2">
                  {food.name}
                </h3>
                <p className="text-pp-stone text-sm leading-relaxed mb-4 flex-1">
                  {food.rationale}
                </p>
                <Link
                  to={`/product/${food.slug}`}
                  className="text-sm font-medium text-pp-terracotta hover:underline"
                >
                  Shop {food.name} &rarr;
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-pp-charcoal text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-display-sm mb-4">
              Meet the First Foods Collection
            </h2>
            <p className="text-pp-sand max-w-lg mx-auto mb-8">
              Give your little one the gift of purposeful sensory exploration
              with our research-informed teethers, designed around the foods
              parents trust most.
            </p>
            <Button
              as={Link}
              to="/shop"
              variant="secondary"
              size="lg"
              className="bg-pp-terracotta hover:bg-pp-terracotta/90 border-pp-terracotta"
            >
              Shop the Collection
              <ArrowRight className="w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
